import { useState, useEffect, useMemo, useCallback } from "preact/hooks";
import emitter from "@webcore/core/events/eventemitter";

// ============================================================================
// XR CHECK STATUS
// ============================================================================
// Capability is the only gate. We don't branch on browser name anymore — if a
// browser can actually start an `immersive-ar` session it's supported (Chrome,
// Edge, Opera, Samsung Internet, …); if it can't, the device/browser simply
// isn't capable. iOS never exposes WebXR but can always hand off to the EyeJack
// App Clip, so any iOS device routes there regardless of browser or private mode.
export enum XRCheckStatus {
  CHECKING = "CHECKING",
  OK = "OK",
  SHOW_APP_CLIP = "SHOW_APP_CLIP",
  NOT_SUPPORTED = "NOT_SUPPORTED",
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
function isIOS(): boolean {
  return (
    /^iP/.test(navigator.platform) ||
    (/^Mac/.test(navigator.platform) && navigator.maxTouchPoints > 4)
  );
}

// The EyeJack App Clip's minimum iOS deployment target. iOS below this can't
// launch the App Clip — Apple shows its own "update to X" wall — so we route
// these devices to NOT_SUPPORTED and show our own screen instead. (iOS has no
// in-browser WebXR, so there's no AR fallback below this version anyway.)
const MIN_IOS_FOR_APP_CLIP = { major: 16, minor: 4 };

// Parse iOS/iPadOS major.minor from the UA. Returns null when it can't be
// determined — notably iPadOS 13+ in desktop mode, which reports a macOS UA
// ("Mac OS X 10_15") with no iOS version. In that case we DON'T block (we let
// the App Clip path proceed) rather than mistake the macOS version for iOS.
function getIOSVersion(): { major: number; minor: number } | null {
  const m = navigator.userAgent.match(
    /(?:iPhone OS|CPU(?: iPhone)? OS) (\d+)_(\d+)/
  );
  if (!m) return null;
  return { major: parseInt(m[1], 10), minor: parseInt(m[2], 10) };
}

// True only when the iOS version is positively known AND below the App Clip min.
function isIOSBelowAppClipMin(): boolean {
  const v = getIOSVersion();
  if (!v) return false;
  if (v.major < MIN_IOS_FOR_APP_CLIP.major) return true;
  return (
    v.major === MIN_IOS_FOR_APP_CLIP.major &&
    v.minor < MIN_IOS_FOR_APP_CLIP.minor
  );
}

// ============================================================================
// INFO MESSAGES
// ============================================================================
const XR_STATUS_MESSAGES: Partial<Record<XRCheckStatus, string>> = {
  [XRCheckStatus.SHOW_APP_CLIP]: "Tap below to open in App Clip.",
  [XRCheckStatus.NOT_SUPPORTED]: "AR is not supported on this device or browser.",
};

// ============================================================================
// Compatibility check
// ============================================================================
interface UseXRCheckInlineReturn {
  status: XRCheckStatus;
  isChecking: boolean;
  isCompatible: boolean;
  statusMessage: string | null;
  showAppClipButton: boolean;
  /** Emit the correct XR error event for an unsupported device/browser, so the
   *  right screen shows. Call from a launch handler when status is NOT_SUPPORTED.
   *  The device/capability → event mapping lives here (universal), not per-app. */
  notifyUnsupported: () => void;
}

export function useXRCheckInline(): UseXRCheckInlineReturn {
  const [status, setStatus] = useState<XRCheckStatus>(XRCheckStatus.CHECKING);

  const isChecking = status === XRCheckStatus.CHECKING;
  const showAppClipButton = status === XRCheckStatus.SHOW_APP_CLIP;
  // iOS reaches AR through the App Clip, so it counts as compatible once the
  // check resolves; Android/desktop are compatible only when WebXR is present.
  const isCompatible =
    status === XRCheckStatus.OK || status === XRCheckStatus.SHOW_APP_CLIP;

  const statusMessage = useMemo(() => XR_STATUS_MESSAGES[status] ?? null, [status]);

  // Surface the "can't run AR" screen. Always 'not-supported' — useXRErrorGuidance
  // is the single source of truth for what to actually show: its
  // showCopyLinkGuidance inspects device + browser capability and renders the
  // "open in Chrome" copy-link (incapable Android browser), the "update to iOS
  // 16.4" copy (iOS), or the generic "not supported on this device" screen. So
  // there's no need to pre-decide the event here (and the old 'undefined' branch
  // only produced the never-wanted "WebXR is not defined in this browser" text).
  const notifyUnsupported = useCallback(() => {
    emitter.emit("xr-error-not-supported");
  }, []);

  // Check on mount
  useEffect(() => {
    let cancelled = false;

    const checkXRSupport = async () => {
      // The single source of truth: can this browser actually start an
      // immersive-ar session? Covers ARCore availability, device support, and
      // every WebXR-capable Chromium browser — no browser-name guessing.
      let hasXR = false;
      try {
        if (navigator.xr?.isSessionSupported) {
          hasXR = await navigator.xr.isSessionSupported("immersive-ar");
        }
      } catch {
        hasXR = false;
      }
      if (cancelled) return;

      if (hasXR) {
        setStatus(XRCheckStatus.OK);
        return;
      }

      // No WebXR. iOS has no in-browser WebXR at all, but every iOS browser
      // (Safari, Chrome iOS, private mode, …) can hand off to the EyeJack App
      // Clip — so route any iOS device there.
      if (isIOS()) {
        // Below the App Clip's minimum iOS → it can't launch (Apple would show
        // its own "update to X" wall), so show our "not supported" screen.
        if (isIOSBelowAppClipMin()) {
          setStatus(XRCheckStatus.NOT_SUPPORTED);
          return;
        }
        setStatus(XRCheckStatus.SHOW_APP_CLIP);
        return;
      }

      // Android without AR capability (no ARCore / non-WebXR browser) or
      // desktop: nothing we can launch into.
      setStatus(XRCheckStatus.NOT_SUPPORTED);
    };

    void checkXRSupport();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    status,
    isChecking,
    isCompatible,
    statusMessage,
    showAppClipButton,
    notifyUnsupported,
  };
}
